import { z } from 'zod'

/**
 * Configuração do painel vinda do ambiente.
 *
 * Invariante nº 8 do CLAUDE.md: falhe no boot se faltar variável obrigatória.
 * Aqui todas têm default de desenvolvimento, mas um valor PRESENTE e inválido
 * (string vazia, por exemplo) derruba a aplicação em vez de virar `undefined`
 * silencioso no meio de uma URL.
 *
 * Só `VITE_*` chega ao navegador: nunca coloque segredo neste arquivo.
 */
const envSchema = z.object({
  /**
   * Base da API. O default relativo `/api` usa o proxy do Vite em dev e o
   * mesmo host em produção (nginx faz o repasse), o que evita CORS.
   */
  VITE_API_BASE_URL: z.string().trim().min(1).default('/api'),
  VITE_APP_NAME: z.string().trim().min(1).default('Locaveri'),
  VITE_DEFAULT_TENANT_SLUG: z.string().trim().min(1).default('locaveri'),
  /**
   * Origem do SITE PÚBLICO — a base do link que o atendente manda ao cliente
   * (a ponte do WhatsApp, Gap 13).
   *
   * É o site e não o painel: o link vai para o CLIENTE, e o cliente não entra
   * aqui. O mesmo endereço serve o app por universal link, então um link só
   * atende quem tem o aplicativo e quem não tem.
   *
   * O default é a porta do site em desenvolvimento (`SITE_PUBLIC_ORIGIN` do
   * `.env.example`). Em produção precisa apontar para o domínio de verdade —
   * um link `localhost` mandado por WhatsApp não abre em aparelho nenhum.
   */
  VITE_SITE_URL: z
    .url()
    .default('http://localhost:5192')
    // Sem barra no fim: `${base}/reservar` com base terminando em barra vira
    // `//reservar`, que alguns servidores tratam como caminho diferente.
    .transform((valor) => valor.replace(/\/+$/, '')),
  /**
   * O fundo do mapa (rastreamento e cerca virtual) — URL de tiles no formato do
   * Leaflet, `{z}/{x}/{y}`.
   *
   * ⚠️ O default é o servidor PÚBLICO do OpenStreetMap, e ele serve para
   * desenvolvimento, não para produção: a política de uso deles proíbe uso
   * pesado e pede servidor próprio ou provedor contratado para aplicação
   * comercial. Em produção isto aponta para o provedor escolhido (MapTiler,
   * Stadia, Mapbox…), e a MESMA origem entra na CSP do nginx
   * (`CSP_ORIGEM_DE_MAPA`) — sem ela, o mapa aparece cinza.
   */
  VITE_MAP_TILE_URL: z
    .string()
    .trim()
    .min(1)
    .default('https://tile.openstreetmap.org/{z}/{x}/{y}.png'),
  /** O crédito que o provedor exige na tela. Obrigatório por licença, não enfeite. */
  VITE_MAP_ATTRIBUTION: z.string().trim().min(1).default('© OpenStreetMap'),
  /** DSN do Sentry. Vazio = observabilidade desligada (o normal em dev). */
  VITE_SENTRY_DSN: z
    .string()
    .trim()
    .default('')
    .transform((value) => (value.length > 0 ? value : undefined)),
})

export type AdminEnv = z.output<typeof envSchema>

function loadEnv(): AdminEnv {
  const raw: unknown = import.meta.env
  const parsed = envSchema.safeParse(raw)

  if (!parsed.success) {
    const problemas = parsed.error.issues
      .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
      .join(' | ')

    throw new Error(`Configuração de ambiente inválida para o painel — ${problemas}`)
  }

  return parsed.data
}

export const env: AdminEnv = loadEnv()
